import { useState, useEffect } from "react";
import { getUserRoles, isTokenExpired } from "../utils/jwtUtils";
import { useUser } from "../components/app/context/UserContext";

/**
 * Custom hook for role-based access control
 * Roles are seeded from localStorage at login (server-provided) and
 * re-synced every 10 minutes via UserContext.validateAndSyncUserRoles,
 * which calls /api/v1/base/roles/my-roles authenticated by HttpOnly cookie.
 * The `userRolesChanged` event keeps this hook's state current within the
 * same tab whenever the server sync corrects or updates the role set.
 */
export const useRoleCheck = (triggerRefresh = null) => {
  const [userRoles, setUserRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { logoutUser } = useUser();

  useEffect(() => {
    const loadRoles = async () => {
      try {
        // Check if authentication is starting or in progress
        if (window.authenticationStarting || window.authenticationPromise) {
          console.log(
            "useRoleCheck: Authentication is starting/in progress, waiting...",
          );
          // Wait a bit for the promise to be created if it's not ready yet
          let attempts = 0;
          while (
            window.authenticationStarting &&
            !window.authenticationPromise &&
            attempts < 10
          ) {
            await new Promise((resolve) => setTimeout(resolve, 50));
            attempts++;
          }

          if (window.authenticationPromise) {
            console.log(
              "useRoleCheck: Waiting for authentication promise to complete...",
            );
            await window.authenticationPromise;
            console.log(
              "useRoleCheck: Authentication complete, proceeding with role check",
            );
          }
        }

        // Check if token is expired
        if (isTokenExpired()) {
          console.warn("JWT token is expired - initiating logout flow");
          setUserRoles([]);
          setIsLoading(false);

          // Initiate logout flow to redirect to login screen
          if (logoutUser) {
            logoutUser();
          }
          return;
        }

        const roles = getUserRoles();
        console.log("useRoleCheck: Loaded roles from localStorage:", roles);
        setUserRoles(roles);
      } catch (error) {
        console.error("Error loading roles from localStorage:", error);
        setUserRoles([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadRoles();

    // Listen for storage changes (in case token is updated in another tab)
    const handleStorageChange = (e) => {
      if (e.key === "authToken" || e.key === "roles") {
        console.log(
          "useRoleCheck: localStorage key",
          e.key,
          "changed in another tab, reloading roles",
        );
        setIsLoading(true);
        loadRoles();
      }
    };

    // Listen for server-side role sync (same tab — fires when validateAndSyncUserRoles
    // detects that the server-authoritative role set differs from what is cached).
    // This is the primary defence against localStorage role manipulation: within 10
    // minutes the server correction propagates here and the UI updates accordingly.
    const handleRolesChanged = (e) => {
      console.log(
        "useRoleCheck: userRolesChanged event — reloading roles from server-synced localStorage",
        e.detail,
      );
      setIsLoading(true);
      loadRoles();
    };

    // Listen for login/logout events from UserContext
    const handleUserLogin = () => {
      console.log("useRoleCheck: User login event detected, reloading roles");
      setIsLoading(true);
      // Small delay to ensure token is saved to localStorage
      setTimeout(() => {
        loadRoles();
      }, 100);
    };

    const handleUserLogout = () => {
      console.log("useRoleCheck: User logout event detected, clearing roles");
      setUserRoles([]);
      setIsLoading(false);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("userLoggedIn", handleUserLogin);
    window.addEventListener("userLoggedOut", handleUserLogout);
    window.addEventListener("userRolesChanged", handleRolesChanged);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("userLoggedIn", handleUserLogin);
      window.removeEventListener("userLoggedOut", handleUserLogout);
      window.removeEventListener("userRolesChanged", handleRolesChanged);
    };
  }, [triggerRefresh, logoutUser]); // Add triggerRefresh and logoutUser as dependencies

  /**
   * Check if user has a specific role
   * @param {string} roleToCheck - Role to check for
   * @returns {boolean}
   */
  const hasRole = (roleToCheck) => {
    if (!roleToCheck) return false;
    console.debug(
      "hasRole(): Checking for role:",
      roleToCheck,
      "in",
      userRoles,
    );
    return userRoles.some(
      (role) => role.toLowerCase() === roleToCheck.toLowerCase(),
    );
  };

  /**
   * Check if user has any of the specified roles
   * @param {string[]} rolesToCheck - Array of roles to check
   * @returns {boolean}
   */
  const hasAnyRole = (rolesToCheck) => {
    if (!Array.isArray(rolesToCheck) || rolesToCheck.length === 0) return false;
    return rolesToCheck.some((roleToCheck) =>
      userRoles.some(
        (role) => role.toLowerCase() === roleToCheck.toLowerCase(),
      ),
    );
  };

  /**
   * Check if user has all of the specified roles
   * @param {string[]} rolesToCheck - Array of roles to check
   * @returns {boolean}
   */
  const hasAllRoles = (rolesToCheck) => {
    if (!Array.isArray(rolesToCheck) || rolesToCheck.length === 0) return false;
    return rolesToCheck.every((roleToCheck) =>
      userRoles.some(
        (role) => role.toLowerCase() === roleToCheck.toLowerCase(),
      ),
    );
  };

  // Specific role checks for common use cases
  const isAccountAdmin = () => {
    return hasAnyRole(["account_admin", "platform_admin"]);
  };

  const isGroupAdmin = () => {
    return hasAnyRole(["group_admin", "account_admin", "platform_admin"]);
  };

  const isPlatformAdmin = () => {
    return hasRole("platform_admin");
  };

  const canAccessAccountManagement = () => {
    return hasAnyRole(["account_admin", "platform_admin"]);
  };

  const canManageGroups = () => {
    return hasAnyRole(["group_admin", "account_admin", "platform_admin"]);
  };

  const canManageUsers = () => {
    return hasAnyRole(["group_admin", "account_admin", "platform_admin"]);
  };

  const canCreateUsers = () => {
    return hasAnyRole(["group_admin", "account_admin", "platform_admin"]);
  };

  /**
   * Check if user is a full-access event demo seat
   * Demo seats have restricted forwarding channels and no billing access
   * @returns {boolean}
   */
  const isDemoSeat = () => {
    return hasRole("demo_seat");
  };

  // Manual refresh function
  const refreshRoles = () => {
    console.log("useRoleCheck: Manual refresh of roles requested");
    setIsLoading(true);
    try {
      const roles = getUserRoles();
      console.log("useRoleCheck: Refreshed roles:", roles);
      setUserRoles(roles);
    } catch (error) {
      console.error("Error refreshing roles:", error);
      setUserRoles([]);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    userRoles,
    isLoading,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    isAccountAdmin,
    isGroupAdmin,
    isPlatformAdmin,
    canAccessAccountManagement,
    canManageGroups,
    canManageUsers,
    canCreateUsers,
    isDemoSeat,
    refreshRoles,
  };
};
